import * as React from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter, RouterHistory} from 'react-router-dom';

import _ from 'underscore';

import {Routing} from 'nutshell-core/routing';
import {SavedFilters} from 'nutshell-core/saved-filters';
import {Session} from 'nutshell-core/session';
import type {SavedFilter, SavedListType, SavedListId, CoreEntityType} from 'nutshell-core/types';

import {SavedFilterModal} from '../../../base/saved-filter-modal/saved-filter-modal';

import {removeListParamFromValue} from '../../../../saved-filters/saved-filters-utils';
import type {NutshellState} from '../../../../store';

import {EmptyView} from './empty-view';
import {SavedListsRow} from './saved-lists-row';

const REPORT_TYPE_NAMES = {
    entity_list_accounts: 'Companies',
    entity_list_contacts: 'People',
    entity_list_leads: 'Leads',
} as const;

type OwnProps = {
    isOpen: boolean;
    entityType: CoreEntityType;
    onClose: () => any;
};
type StateProps = {
    savedLists: Partial<Record<SavedListId, SavedFilter<SavedListType>>>;
    loadingErrorMessage?: string;
    actionErrorMessage?: string;
    isLoading: boolean;
    sessionUserId: string | null | undefined;
};
type DispatchProps = {
    fetchSavedLists: () => any;
    onDelete: (savedList: SavedFilter<SavedListType>) => any;
    onActionErrorHidden: () => any;
};
type RouterState = {
    history: RouterHistory;
};
type RoutedProps = OwnProps & ContextRouter;
type Props = OwnProps & RouterState & StateProps & DispatchProps;

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        fetchSavedLists: () => {
            dispatch(SavedFilters.fetchSavedLists());
        },
        onDelete: (savedList) => {
            return dispatch(SavedFilters.deleteSavedFilter(savedList));
        },
        onActionErrorHidden: () => {
            dispatch(SavedFilters.clearActionError());
        },
    };
}

const mapStateToProps = (state: NutshellState): StateProps => {
    return {
        savedLists: SavedFilters.getSavedLists(state),
        loadingErrorMessage: SavedFilters.getLoadingErrorMessage(state),
        actionErrorMessage: SavedFilters.getActionErrorMessage(state),
        isLoading: SavedFilters.getIsLoading(state),
        sessionUserId: Session.getSessionStateUserId(state),
    };
};

function mergeProps(
    stateProps: StateProps,
    dispatchProps: DispatchProps,
    ownProps: RoutedProps
): Props {
    // Strip out unneeded location and match props
    const {location, match, ...restOwnProps} = ownProps; // eslint-disable-line @typescript-eslint/no-unused-vars

    return {
        ...stateProps,
        ...dispatchProps,
        ...restOwnProps,
    };
}

export class SavedListsModalComponent extends React.PureComponent<Props> {
    render() {
        const savedLists = _.values(this.props.savedLists);
        const bucketedSavedLists = SavedFilters.utils.bucketSavedListsByType(
            // @ts-expect-error
            savedLists,
            this.props.entityType
        );

        return (
            <SavedFilterModal
                headerText='Saved lists'
                searchPlaceholder='Search for a saved list…'
                getExtraSearchableText={getEntityTypeName}
                savedFilters={bucketedSavedLists}
                isOpen={this.props.isOpen}
                onClose={this.props.onClose}
                isLoading={this.props.isLoading}
                isErrored={Boolean(this.props.loadingErrorMessage)}
                errorMessage={this.props.actionErrorMessage}
                onErrorHidden={this.props.onActionErrorHidden}
                sessionUserId={this.props.sessionUserId}
                renderFilterRow={this.renderRow}
                renderEmptyView={this.renderEmptyView}
                renderNoPersonalSavedFilters={this.renderNoPersonalSavedLists}
            />
        );
    }

    renderNoPersonalSavedLists = () => {
        return (
            <EmptyView
                isLoading={this.props.isLoading}
                onErrorRetry={this.handleErrorRetry}
                hasError={Boolean(this.props.loadingErrorMessage)}
                emptyText={"You haven't saved any lists yet"}
            />
        );
    };

    renderEmptyView = () => {
        // If there are savedLists, it means that the user has searched,
        // and none were found
        const emptyText = Object.keys(this.props.savedLists).length
            ? 'Couldn’t find any saved lists.'
            : 'No saved lists.';

        return (
            <EmptyView
                isLoading={this.props.isLoading}
                onErrorRetry={this.handleErrorRetry}
                hasError={Boolean(this.props.loadingErrorMessage)}
                emptyText={emptyText}
            />
        );
    };

    renderRow = (savedList: SavedFilter<SavedListType>, isSelected: boolean) => {
        return (
            <SavedListsRow
                key={savedList.id}
                savedList={savedList}
                isSelected={isSelected}
                onSelect={this.props.onClose}
                onDelete={this.handleDelete}
                sessionUserId={this.props.sessionUserId}
            />
        );
    };

    handleErrorRetry = () => {
        this.props.fetchSavedLists();
    };

    handleDelete = (savedListId: SavedListId) => {
        const savedList = this.props.savedLists[savedListId];
        // @ts-expect-error
        this.props.onDelete(savedList).then(this.handleDeleteSuccess);
    };

    handleDeleteSuccess = (savedList: SavedFilter<SavedListType>) => {
        const searchString = window.location.search.replace(/^\?/, '');
        const currentListId = Routing.deparam(searchString).list;
        if (currentListId === savedList.id) {
            const newSearchString = removeListParamFromValue(searchString);
            this.props.history.replace({search: newSearchString});
        }
    };
}

// @ts-expect-error
const connector = connect<Props, RoutedProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
);

// @ts-expect-error
export const SavedListsModal = withRouter<OwnProps>(connector(SavedListsModalComponent));

// @ts-expect-error
const getEntityTypeName = (savedList): string => {
    const savedListTitle = getEntityListType(savedList);

    return savedListTitle;
};

// @ts-expect-error
const getEntityListType = (savedList) => REPORT_TYPE_NAMES[savedList.reportType];
